import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useCommon } from '../../contexts/CommonContext';
import { useTenant } from '../../contexts/TenantContext';
import TableWithPagination from '../../components/TableWithPagination/TableWithPagination';
import PageHeader from '../../components/PageHeader/PageHeader';
import PageContainer from '../../components/PageContainer/PageContainer';
import EmptyState from '../../components/EmptyState/EmptyState';
import { PipelineApi } from '../../api/PipelineApi';
import { showAlert, showToast } from '../../api/CommonApi';
import { MoreOptionsButton, SearchInput } from './styles';
import { Catalog, PipelineStatus } from '../../models/pipeline/Pipeline';

// Type alias para maior clareza no contexto de pipeline
type Pipeline = Catalog;
import {
  BdsButton,
  BdsDropdown,
  BdsGrid,
  BdsList,
  BdsListItem,
  BdsTableCell,
  BdsTableRow,
  BdsTypo,
} from 'blip-ds/dist/blip-ds-react';
import { useContentLocalizer } from '../../hooks/useContentLocalizer';
import { usePaginatedSearch } from '../../hooks/usePaginatedSearch';
import { useTableStates } from '../../hooks/useTableStates';
import { localization } from './localization';
import { calculateOffset } from '../../utils/paginationUtils';
import { navigateToExternal } from '../../utils/navigationUtils';
import { getStatusBadgeStyles, StatusType } from '../../utils/statusBadgeUtils';
import { useLocalizedBreadcrumbs } from '../../utils/breadcrumbUtils';

// Constants
const PAGE_OPTIONS = [5, 10, 15];
const CREATE_PIPELINE_URL = '/application/create/pipelinename/';

type DeletePipelineAlertResponse = { response: boolean };

const Pipeline: React.FC = () => {
  const {
    setHeaderContent,
    common,
  } = useCommon();
  const { tenant } = useTenant();
  const translate = useContentLocalizer(localization);

  // Pagination and search hook
  const {
    searchTerm,
    debouncedSearchTerm,
    page,
    itemsPerPage,
    loadingState,
    setLoadingState,
    handleSearchChange,
    handlePaginationChange,
    handleItemsPerPageChange,
  } = usePaginatedSearch({ pageOptions: PAGE_OPTIONS });

  // Component mounted flag to prevent state updates after unmount
  const isMountedRef = useRef(true);

  // State management
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [totalPipelines, setTotalPipelines] = useState(0);
  const [pipelineConfigs, setPipelineConfigs] = useState<Map<string, any>>(new Map());

  // Computed values
  const { isLoading, hasError } = loadingState;
  const tenantId = tenant?.info?.id;

  const computedStates = useTableStates({
    loadingState,
    itemsLength: pipelines.length,
    searchTermLength: debouncedSearchTerm.length,
  });


  // API handlers
  const fetchPipelines = useCallback(async (): Promise<void> => {
    if (!tenantId || !itemsPerPage || !page) return;

    setLoadingState({ isLoading: true, hasError: false });
    try {
      const applicationsData = await PipelineApi.getApplicationsAsync(tenantId);
      const pipelineApps = applicationsData.items?.filter((app: any) => app.template === 'pipeline') || [];
      
      const sortedPipelineApps = pipelineApps.sort((a: any, b: any) => {
        const dateA = new Date(a.created || 0);
        const dateB = new Date(b.created || 0);
        return dateB.getTime() - dateA.getTime();
      });
      const mappedPipelines = sortedPipelineApps.map((app: any) => new Catalog({
        id: app.shortName,
        name: app.name,
        pipeline_id: app.shortName,
        tasks_count: 0,
        status: app.hasPermission ? PipelineStatus.PUBLISHED : PipelineStatus.UNPUBLISHED,
        updated_at: app.updated,
        updated_by: app.emailOwner,
        hasPermission: app.hasPermission,
      }));

      const filteredPipelines = debouncedSearchTerm 
        ? mappedPipelines.filter((item: Pipeline) => 
            item.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
            item.pipeline_id.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
          )
        : mappedPipelines;
      const startIndex = calculateOffset(page, itemsPerPage);
      const endIndex = startIndex + itemsPerPage;
      const paginatedItems = filteredPipelines.slice(startIndex, endIndex);

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setPipelines(paginatedItems);
        setTotalPipelines(filteredPipelines.length);
        setLoadingState({ isLoading: false, hasError: false });
      }
      
      if (paginatedItems.length > 0) {
        fetchPipelineConfigurations(paginatedItems);
      }

    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching pipelines:', error);
      if (isMountedRef.current) {
        setLoadingState({ isLoading: false, hasError: true });
      }
    }
  }, [tenantId, itemsPerPage, page, debouncedSearchTerm]);

  const fetchPipelineConfigurations = useCallback(async (pipelines: Pipeline[]): Promise<void> => {
    const { loggedUser } = common;
    const localNode = loggedUser?.localNode;
    const userEmail = loggedUser?.email || loggedUser?.identity;
    
    const pipelinesWithPermission = pipelines.filter(pipeline => pipeline.hasPermission);
    
    const configPromises = pipelinesWithPermission.map(async (pipeline) => {
      try {
        const config = await PipelineApi.getBuilderWorkingConfigurationAsync(pipeline.id, localNode, userEmail);
        return { pipelineId: pipeline.id, config };
      } catch (error) {
        console.error('Error fetching configuration for pipeline', pipeline.id, ':', error);
        return { pipelineId: pipeline.id, config: null };
      }
    });

    const results = await Promise.all(configPromises);
    const newConfigs = new Map(pipelineConfigs);
    
    results.forEach(({ pipelineId, config }) => {
      if (config) {
        newConfigs.set(pipelineId, config);
      }
    });
    
    if (isMountedRef.current) {
      setPipelineConfigs(newConfigs);
    }
  }, [common.loggedUser, pipelineConfigs]);

  // Event handlers

  const handleCreatePipeline = useCallback(async (): Promise<void> => {
    await navigateToExternal(CREATE_PIPELINE_URL, tenantId);
  }, []);

  const handleOpenPipeline = useCallback(
    async (pipelineId: string): Promise<void> => {
      await navigateToExternal(`/application/detail/${pipelineId}/templates/pipeline`, tenantId);
    },
    [],
  );

  const handleManageTeam = useCallback(
    async (pipelineId: string): Promise<void> => {
      await navigateToExternal(`/application/detail/${pipelineId}/team`, tenantId);
    },
    [],
  );

  const handleLogs = useCallback(
    async (pipelineId: string): Promise<void> => {
      await navigateToExternal(`/application/detail/${pipelineId}/builder-observability/builder-monitoring`, tenantId);
    },
    [],
  );

  const showDeletePipelineAlert = useCallback(
    (): Promise<DeletePipelineAlertResponse> =>
      showAlert({
        ...translate.deletePipelineAlert,
        variant: 'delete',
        icon: 'trash',
      }),
    [],
  );

  const handleDeletePipeline = useCallback(
    async (pipelineId: string) => {
      const confirm = await showDeletePipelineAlert();
      if (!confirm.response) return;
      try {
        setLoadingState((prev) => ({ ...prev, isLoading: true }));
        await PipelineApi.deleteApplicationAsync(pipelineId);
        showToast({
          ...translate.successDeletePipelineToast,
          type: 'success',
        });
        fetchPipelines();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error deleting pipeline:', error);
        setLoadingState((prev) => ({ ...prev, isLoading: false }));
        showToast({
          ...translate.errorDeletePipelineToast,
          type: 'danger',
        });
      }
    },
    [fetchPipelines, showDeletePipelineAlert],
  );

  // Effects
  useEffect(() => {
    fetchPipelines();

    // Cleanup function to mark component as unmounted
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchPipelines]);

  const breadcrumbData = useLocalizedBreadcrumbs('pipeline');

  useEffect(() => {
    if (breadcrumbData) {
      const { breadcrumbItems } = breadcrumbData;

      setHeaderContent({
        breadcrumbItems,
      });
    }
    return () => setHeaderContent({ redirect: null });
  }, [setHeaderContent, breadcrumbData]);

  // Helper functions for pipeline data
  const getPipelineTasks = useCallback((pipeline: Pipeline): string | number => {
    if (!pipeline.hasPermission) return '-';
    const config = pipelineConfigs.get(pipeline.id);
    const statesCount = config?.['builder:statesCount'];
    return parseInt(statesCount) || 0;
  }, [pipelineConfigs]);

  const getPipelineStatus = useCallback((pipeline: Pipeline): StatusType => {
    if (!pipeline.hasPermission) return 'Despublicada' as StatusType;
    const config = pipelineConfigs.get(pipeline.id);
    const status = config?.['builder:statesPublish'];
    return (status === 'Published' ? PipelineStatus.PUBLISHED : PipelineStatus.UNPUBLISHED) as StatusType;
  }, [pipelineConfigs]);

  const getTranslatedStatus = useCallback((status: StatusType | '-'): string => {
    if (status === '-') return status;
    return status === PipelineStatus.PUBLISHED
      ? translate.pipelineStatus.published
      : translate.pipelineStatus.unpublished;
  }, [translate.pipelineStatus]);

  // Render helpers
  const renderPipelineRow = useCallback(
    (item: Pipeline) => (
      <BdsTableRow key={item.id} style={!item.hasPermission ? { opacity: 0.5 } : {}}>
        <BdsTableCell>
          <BdsTypo variant="fs-14" bold="bold" style={!item.hasPermission ? { color: '#999' } : {}}>
            {item.name}
          </BdsTypo>
        </BdsTableCell>
        <BdsTableCell>
          <BdsTypo variant="fs-14" style={!item.hasPermission ? { color: '#999' } : {}}>{item.pipeline_id || `Pipe-${item.id?.slice(-7)}`}</BdsTypo>
        </BdsTableCell>
        <BdsTableCell>
          <BdsTypo variant="fs-14" style={!item.hasPermission ? { color: '#999' } : {}}>{getPipelineTasks(item)}</BdsTypo>
        </BdsTableCell>
        <BdsTableCell>
          {item.hasPermission ? (
            <span style={getStatusBadgeStyles(getPipelineStatus(item))}>
              {getTranslatedStatus(getPipelineStatus(item))}
            </span>
          ) : (
            <BdsTypo variant="fs-14" style={{ color: '#999' }}>{getTranslatedStatus(getPipelineStatus(item))}</BdsTypo>
          )}
        </BdsTableCell>
        <BdsTableCell>
          {item.hasPermission ? (
            <BdsDropdown>
              <div slot="dropdown-activator">
                <MoreOptionsButton name="more-options-vertical" />
              </div>
              <div slot="dropdown-content">
                <BdsList type-list="default">
                  <BdsListItem onClick={() => handleOpenPipeline(item.id)} text={translate.pipelineActions.open} clickable icon="folder" />
                  <BdsListItem onClick={() => handleManageTeam(item.id)} text={translate.pipelineActions.team} clickable icon="team" />
                  <BdsListItem onClick={() => handleLogs(item.id)} text={translate.pipelineActions.logs} clickable icon="bug-report" />
                  <BdsListItem onClick={() => handleDeletePipeline(item.id)} text={translate.pipelineActions.delete} clickable icon="trash" />
                </BdsList>
              </div>
            </BdsDropdown>
          ) : null}
        </BdsTableCell>
      </BdsTableRow>
    ),
    [handleDeletePipeline, handleOpenPipeline, handleManageTeam, getPipelineTasks, getPipelineStatus, getTranslatedStatus, handleLogs],
  );

  const renderEmptyState = useCallback(
    () => (
      <EmptyState
        {...translate.emptyState}
        primaryButton={{
          label: translate.createPipelineButton,
          onClick: handleCreatePipeline,
        }}
      />
    ),
    [handleCreatePipeline],
  );

  const renderTableContent = useCallback(
    () => (
      <BdsGrid direction="column" gap="4">
        <SearchInput
          onBdsChange={handleSearchChange}
          icon="search"
          placeholder={translate.searchPlaceholder}
          value={searchTerm}
        />
        <TableWithPagination
          options={{
            heading: [
              translate.tableHeaders.pipeline,
              translate.tableHeaders.id,
              translate.tableHeaders.tasks,
              translate.tableHeaders.status,
              '',
            ],
            itemsPerPageList: PAGE_OPTIONS,
            itemsPerPage,
            numberOfItems: totalPipelines,
            isLoading,
            hasLoadingError: hasError,
            hasEmptyState: computedStates.hasSearchResults,
          }}
          onRefreshData={fetchPipelines}
          onPaginationChange={handlePaginationChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        >
          {pipelines.length > 0 && pipelines.map(renderPipelineRow)}
        </TableWithPagination>
      </BdsGrid>
    ),
    [
      handleSearchChange,
      searchTerm,
      itemsPerPage,
      totalPipelines,
      isLoading,
      hasError,
      computedStates.hasSearchResults,
      fetchPipelines,
      handlePaginationChange,
      handleItemsPerPageChange,
      pipelines,
      renderPipelineRow,
    ],
  );


  // Early return for missing tenant
  if (!tenantId) return null;

  return (
    <PageContainer>
      <BdsGrid direction="column" gap="4">
        <PageHeader title={translate.title} subtitle={translate.subtitle}>
          <BdsButton onClick={handleCreatePipeline}>{translate.createPipelineButton}</BdsButton>
        </PageHeader>
        {computedStates.hasNoItems ? renderEmptyState() : renderTableContent()}
      </BdsGrid>
    </PageContainer>
  );
};

export default Pipeline;
